import { useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Snackbar from '@mui/material/Snackbar';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { alpha } from '@mui/material/styles';
import { SeoHead } from '@/shared/components/SeoHead';
import { Button } from '@/shared/components/ui';
import { BrandLogo } from '@/shared/components/BrandLogo';
import { GuideIdentityStatus } from '@/types';
import { guideIdentityApi, guideIdentityKeys } from '../api/guide-identity.api';
import { GuideIdentityCard } from '../components/GuideIdentityCard';
import { GuideProfileDetails } from '../components/GuideProfileDetails';
import { profileIdFromUrl, toGuideCardData } from '../utils/guideCardData';
import { buildIdentityUrl, copyTextToClipboard } from '../utils/identityUrl';

const CONTENT_MAX_WIDTH = 760;

export function GuideIdentityPublicPage() {
  const { identityCode = '' } = useParams<{ identityCode: string }>();
  const [toast, setToast] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: guideIdentityKeys.public(identityCode),
    queryFn: () => guideIdentityApi.getPublic(identityCode),
    enabled: Boolean(identityCode),
    staleTime: 30_000,
  });

  if (isLoading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }} aria-busy>
        <Skeleton variant="rounded" sx={{ width: '100%', maxWidth: CONTENT_MAX_WIDTH, mx: 'auto', aspectRatio: '85.6 / 54', height: 'auto' }} />
      </Container>
    );
  }

  if (isError || !data) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <SeoHead title="Guide identity not found — trplin" description="This Trplin Guide identity could not be found." />
        <Alert severity="error" sx={{ mb: 2 }}>
          This Trplin Guide identity could not be found.
        </Alert>
        <Button component={RouterLink} to="/guides" intent="primary-dark">
          Browse guides
        </Button>
      </Container>
    );
  }

  // The API only returns guide details for active, approved identities.
  const isActive = data.identity.status === GuideIdentityStatus.ACTIVE && Boolean(data.guide);
  const card = isActive ? toGuideCardData(data.identity, data.guide, profileIdFromUrl(data.profileUrl)) : null;
  const identityUrl = buildIdentityUrl(data.identity.code);
  const isRevoked = data.identity.status === GuideIdentityStatus.REVOKED;

  const seoTitle = card ? `${card.name} — Verified Trplin Guide` : `Trplin Guide Identity — ${data.identity.guideId}`;
  const seoDescription = card
    ? `Verify ${card.name}'s Trplin Guide identity.`
    : isRevoked
      ? 'This Trplin Guide identity is no longer active.'
      : 'Guide identity currently unavailable.';

  const handleCopy = async () => {
    const ok = await copyTextToClipboard(identityUrl);
    setToast(ok ? 'Identity link copied' : 'Unable to copy link');
  };

  return (
    <Box
      sx={{
        minHeight: '70vh',
        background: (t) =>
          `radial-gradient(ellipse 80% 60% at 50% 0%, ${alpha(t.palette.primary.main, 0.12)} 0%, transparent 55%), ${t.palette.grey[50]}`,
        pb: 8,
      }}
    >
      <SeoHead title={seoTitle} description={seoDescription} ogImage={data.guide?.photo} ogUrl={identityUrl} />

      <Container maxWidth="md" sx={{ pt: 3, pb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <BrandLogo size="md" surface="light" />
        </Box>

        {card && data.guide ? (
          <>
            <Box component="header" sx={{ textAlign: 'center', mb: 3, maxWidth: 560, mx: 'auto' }}>
              <Typography component="h1" variant="h4" fontWeight={700} sx={{ mb: 1 }}>
                Trplin Guide Identity
              </Typography>
              <Typography color="text.secondary">
                This card confirms {card.name} is a verified Trplin guide. Scan its QR code any time to check
                it is still valid.
              </Typography>
            </Box>

            <GuideIdentityCard card={card} maxWidth={CONTENT_MAX_WIDTH} />

            {/* View-only: downloads and sharing live in the guide's and admin's dashboards. */}
            {data.profileUrl ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Button
                  component={RouterLink}
                  to={data.profileUrl}
                  intent="secondary-outlined"
                  startIcon={<OpenInNewIcon />}
                >
                  View full profile
                </Button>
              </Box>
            ) : null}

            <Box sx={{ maxWidth: CONTENT_MAX_WIDTH, mx: 'auto', mt: 4 }}>
              <GuideProfileDetails guide={data.guide} />
            </Box>
          </>
        ) : (
          <Box sx={{ maxWidth: 560, mx: 'auto' }}>
            <Alert severity={isRevoked ? 'error' : 'warning'} sx={{ mb: 2 }}>
              {isRevoked
                ? 'This Trplin Guide identity is no longer active.'
                : 'Guide identity currently unavailable.'}
            </Alert>
            <Paper variant="outlined" sx={{ p: 2.5, mb: 2, borderRadius: 3 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Guide ID
              </Typography>
              <Typography fontWeight={800} sx={{ letterSpacing: '0.03em' }}>
                {data.identity.guideId}
              </Typography>
            </Paper>
            <Button intent="secondary-outlined" startIcon={<ContentCopyIcon />} onClick={handleCopy} fullWidth>
              Copy Identity Link
            </Button>
          </Box>
        )}
      </Container>

      <Snackbar open={Boolean(toast)} autoHideDuration={2500} onClose={() => setToast(null)} message={toast} />
    </Box>
  );
}
